import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { CustomerLoginModal } from '@components/Login/CustomerLoginModal';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  type BookingRecordDetailsCustomer,
  type BookingUpdateOfferPublicRecord,
  BookingUpdateOfferStatus
} from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiButton, NoddiInfoBanner } from 'noddi-ui-web';
import { CustomDateFormats, customerRoutes, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { useAuthContext } from 'noddi-web';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { getWidenUiBounds } from '../../utils/bookingUpdateOfferSectionUtils';
import { getBookingUpdateOfferStatusLabel } from './bookingUpdateOfferStatusLabel';
import { BookingUpdateOfferActions } from './components/BookingUpdateOfferActions';
import { BookingUpdateOfferWidenFlow } from './components/BookingUpdateOfferWidenFlow';

interface Props {
  booking: BookingRecordDetailsCustomer;
}

const getOfferSubtitle = ({
  booking,
  deadlineLabel,
  offer
}: {
  booking: BookingRecordDetailsCustomer;
  deadlineLabel: string;
  offer: BookingUpdateOfferPublicRecord;
}) => {
  const statusLabel = getBookingUpdateOfferStatusLabel(offer.status);

  if (offer.status === BookingUpdateOfferStatus.ACCEPTED || offer.status === BookingUpdateOfferStatus.DECLINED) {
    return t`Thanks for responding! Status: ${statusLabel}.`;
  }

  const brandName = booking.customerBrand.name;

  if (offer.reasonCategory === 'sickness') {
    return t`Because of illness on our team, we hope you can make a small change to your appointment with ${brandName}. Please answer within ${deadlineLabel}.`;
  }

  return t`Due to unforeseen circumstances, we kindly ask you to change your booking. Please answer within ${deadlineLabel}.`;
};

export const BookingUpdateOfferContent = ({ booking }: Props) => {
  const { isLoggedIn } = useAuthContext();
  const navigate = useNavigate();
  const [showWidenFlow, setShowWidenFlow] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'widen' | 'reschedule' | 'decline' | null>(null);

  const backPath = customerRoutes.bookingInfo.getPath({ slug: booking.slug });

  const {
    data: offer,
    isPending: isOfferPending,
    error: offerError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingActiveBookingUpdateOffer,
    input: { slug: booking.slug }
  });

  if (isOfferPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (offerError != null) {
    return <ApiErrorPageWithTrans error={offerError} />;
  }

  if (!offer) {
    return (
      <ContentWrapperCustomerApp title={t`No active request`}>
        <NoddiBasicCard>
          <p className='text-sm text-muted-foreground'>
            <Trans>There's no active request for this booking.</Trans>
          </p>
        </NoddiBasicCard>
      </ContentWrapperCustomerApp>
    );
  }

  if (offer.expired) {
    return <Navigate to={backPath} replace />;
  }

  const mismatch = offer.booking.slug !== booking.slug && offer.bookingId !== booking.id;

  if (mismatch) {
    return (
      <ContentWrapperCustomerApp>
        <NoddiInfoBanner
          variant='error'
          texts={[
            {
              value: t`This request doesn't match this booking. Check the link or open the booking from the message we sent you.`
            }
          ]}
        />
      </ContentWrapperCustomerApp>
    );
  }

  const showActions = offer.status === BookingUpdateOfferStatus.PENDING;
  const deadlineLabel = noddiFormatDate(offer.activePeriodEndsAt, NoddiDateFormats.DATE_TIME);

  const invalidateAndNavigateBack = async () => {
    await invalidateQueryExactMatch({ urlKey: URLKeys.getBookingInfo, input: { slug: booking.slug } });
    await invalidateQueryExactMatch({
      urlKey: URLKeys.getBookingActiveBookingUpdateOffer,
      input: { slug: booking.slug }
    });
    navigate(backPath);
  };

  const requireLogin = (action: 'widen' | 'reschedule' | 'decline') => {
    if (!isLoggedIn) {
      setPendingAction(action);
      setShowLoginModal(true);
      return true;
    }

    return false;
  };

  const handleLoginSuccess = () => {
    const action = pendingAction;
    setPendingAction(null);

    if (action === 'widen') {
      setShowWidenFlow(true);
    } else if (action === 'reschedule') {
      navigate(customerRoutes.editMyBookingTimeWindow.getPath({ slug: booking.slug }));
    }
    // 'decline' — user is now logged in, they'll confirm via the dialog again
  };

  const { currentStart, currentEnd } = getWidenUiBounds({ offer, booking });
  const serviceDateLabel = noddiFormatDate(offer.serviceDate, CustomDateFormats.WEEKDAY_WITH_NUMBER_AND_MONTH);

  return (
    <ContentWrapperCustomerApp
      backButtonPath={backPath}
      title={t`Scheduling request`}
      subtitle={getOfferSubtitle({ booking, deadlineLabel, offer })}
    >
      <div className='flex flex-col gap-4'>
        <NoddiBasicCard size='small'>
          <p className='flex items-center gap-2 text-sm'>
            <NoddiIcon name='Clock' size='small' />
            <span>
              <Trans>Your current window:</Trans>{' '}
              <span className='font-semibold'>
                {serviceDateLabel}, {currentStart} – {currentEnd}
              </span>
            </span>
          </p>
        </NoddiBasicCard>

        {showActions ? (
          showWidenFlow && offer.widenOffered ? (
            <BookingUpdateOfferWidenFlow
              booking={booking}
              offer={offer}
              onBack={() => setShowWidenFlow(false)}
              onRequireLogin={requireLogin}
              onSuccess={invalidateAndNavigateBack}
            />
          ) : (
            <BookingUpdateOfferActions
              bookingSlug={booking.slug}
              offer={offer}
              onShowWidenFlow={() => setShowWidenFlow(true)}
              onRequireLogin={requireLogin}
              onSuccess={invalidateAndNavigateBack}
            />
          )
        ) : (
          // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs navigate(); React discards the returned promise, so a rejection is unhandled unless the body catches
          <NoddiButton variant='outline' startIcon='ArrowLeft' onClick={() => navigate(backPath)}>
            <Trans>Back to booking</Trans>
          </NoddiButton>
        )}
      </div>

      <CustomerLoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
        title={t`Sign in to continue`}
        description={t`Sign in to respond to this request`}
      />
    </ContentWrapperCustomerApp>
  );
};
